import { PropsWithChildren, useEffect, useMemo, useState } from 'react';

import dayjs from 'dayjs';
import 'dayjs/plugin/duration';
import 'dayjs/plugin/relativeTime';

import { TIME, CAST_AI } from '@cast/constants';
import { Tooltip, Typography, Box, Stack } from '@cast/design-system';
import {
  isAKSCluster,
  isClusterManaged,
} from '@cast/shared-cluster-context-data-access';
import { TooltipError } from '@cast/shared-messages-ui';
import {
  AgentStatus,
  Cluster,
  ClusterDisplayStatus,
  ClusterStatus,
} from '@cast/types';

const agentStatusLabelMap: Record<AgentStatus, string> = {
  [AgentStatus.CONNECTING]: 'connecting',
  [AgentStatus.ONLINE]: 'online',
  [AgentStatus.NOT_RESPONDING]: 'not responding',
  [AgentStatus.DISCONNECTING]: 'disconnecting',
  [AgentStatus.DISCONNECTED]: 'disconnected',
};

const getTimeFormat = (ms: number) => {
  if (ms < 60_000) {
    return 'ss';
  }

  if (ms < 3_600_000) {
    return 'mm:ss';
  }

  return TIME;
};

type Props = {
  cluster: Cluster;
  title?: string;
};

export const ClusterStatusTooltip = ({
  cluster,
  title,
  children,
}: PropsWithChildren<Props>) => {
  const [relativeTime, setRelativeTime] = useState<string>(
    dayjs(cluster.agentSnapshotReceivedAt).fromNow(true)
  );

  useEffect(() => {
    let secondsDiff = dayjs().diff(cluster.agentSnapshotReceivedAt, 'ms');

    if (
      secondsDiff < 86_400_000 &&
      cluster.displayStatus === ClusterDisplayStatus.NOT_RESPONDING
    ) {
      const timer = setInterval(() => {
        secondsDiff += 1000;
        setRelativeTime(
          `${dayjs.duration(secondsDiff).format(getTimeFormat(secondsDiff))} ${
            secondsDiff < 60_000 ? 'seconds' : ''
          }`
        );
      }, 1000);

      return () => {
        clearInterval(timer);
      };
    }
    return undefined;
  }, [cluster.agentSnapshotReceivedAt, cluster.displayStatus]);

  const statusTooltipContent = useMemo(() => {
    const managed = isClusterManaged(cluster);
    const error = cluster.reconcileError;

    if (
      cluster.status === ClusterStatus.READY &&
      cluster.agentStatus !== AgentStatus.ONLINE
    ) {
      return (
        <Typography variant="P12R">
          The cluster is connected to {CAST_AI}, but the agent is currently{' '}
          <Typography variant="P12B" component="span">
            {agentStatusLabelMap[cluster.agentStatus]}
          </Typography>
          .
        </Typography>
      );
    }

    if (cluster.displayStatus === ClusterDisplayStatus.DISCONNECTED) {
      return (
        <Typography variant="P12R">
          Disconnected from:{' '}
          <Typography variant="P12B" component="span">
            {dayjs(cluster.agentSnapshotReceivedAt).format('YYYY-MM-DD h:ma')}
          </Typography>
        </Typography>
      );
    }

    if (cluster.displayStatus === ClusterDisplayStatus.NOT_FOUND) {
      return (
        <Stack gap={8}>
          <Typography variant="P12R">
            Cluster cannot be reached in the connected cloud account. It may
            have been deleted or credentials are invalid.
          </Typography>
          <Typography variant="P12R">
            You can remove this cluster from {CAST_AI} or update cloud
            credentials if it should remain connected.
          </Typography>
        </Stack>
      );
    }

    if (cluster.displayStatus === ClusterDisplayStatus.NOT_RESPONDING) {
      return (
        <>
          <Typography variant="P12R">
            Unable to reach {CAST_AI} agent for:{' '}
            <Typography variant="P12B" component="span">
              {relativeTime}
            </Typography>
          </Typography>

          <Typography variant="P12R" display="block">
            Please check the status of {CAST_AI} agent pod
          </Typography>
        </>
      );
    }

    if (
      [
        ClusterDisplayStatus.WAITING_CONNECTION,
        ClusterDisplayStatus.CONNECTING,
      ].includes(cluster.displayStatus) &&
      managed
    ) {
      const isAKS = isAKSCluster(cluster);

      return (
        <Typography variant="P12R">
          {isAKS
            ? `Enabling access and setting up ${CAST_AI}. It might take up to 15 minutes. `
            : `Enabling access and setting up ${CAST_AI}`}
        </Typography>
      );
    }

    if (error) {
      return (
        <TooltipError
          title={
            managed
              ? 'We are trying to fix this recurring issue:'
              : 'There is recurring issue:'
          }
          error={error}
        />
      );
    }
    return undefined;
  }, [cluster, relativeTime]);

  const tooltipTitle = useMemo(() => {
    if (!title && !statusTooltipContent) {
      return undefined;
    }

    if (title && !statusTooltipContent) {
      return title;
    }

    if (!title && statusTooltipContent) {
      return statusTooltipContent;
    }

    return (
      <Stack gap={8}>
        {title}
        {statusTooltipContent}
      </Stack>
    );
  }, [statusTooltipContent, title]);

  return (
    <Tooltip arrow placement="top-start" title={tooltipTitle}>
      <Box component="span" sx={{ display: 'inline-flex', lineHeight: 0 }}>
        {children}
      </Box>
    </Tooltip>
  );
};
